/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useLazyQuery } from '@apollo/client';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import {
	Badge,
	Box,
	Button,
	Center,
	Checkbox,
	Container,
	Flex,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	Text,
	useBreakpointValue,
	useDisclosure,
} from '@chakra-ui/react';
import { faFileExcel, faPlus, faPrint, faSlidersH } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table';
import { useEffect, useState } from 'react';
import ReactPaginate from 'react-paginate';
import BreadcrumbComponent from '../../components/BreadcrumbComponent/BreadcrumbComponent';
import PrintLayout from '../../components/Print/PrintLayout';
import LoadingResponsive from '../../components/Table/LoadingResponsive';
import MissionRecordFilter from '../../components/Filter/MissionRecordFilter';
import datePickerFromToToDate from '../../utils/datePickerFromToToDate';
import useStore from '../../store';
import moment from 'moment';
import c from '../../constant';
import MissionRecordAdd from './add';
import MissionRecordExport from './export';

const CATEGORY_LABELS = {
	RequestForMeetingParticipation: 'Request for Meeting Participation (Phnom Penh)',
	Mission: 'Mission (Domestic and International)',
	AttendanceRequest: 'Attendance Request: Forgot to Check-in/Check-out',
};

const QUERY_DATA = gql`
	query get(
		$limit: Float
		$offset: Float
		$no: Float
		$reason: String
		$category: MissionRecordCategoryEnumType
		$fromDate: DateTime
		$toDate: DateTime
		$read: Boolean
		$step: MissionRecordStepType
		$status: MissionRecordStatusType
	) {
		getMissionRecord(
			pagerInput: { limit: $limit, offset: $offset }
			reason: $reason
			no: $no
			category: $category
			fromDate: $fromDate
			toDate: $toDate
			read: $read
			step: $step
			status: $status
		) {
			records {
				id
				no
				category
				reason
				status
				step
				createdBy
				createdAt
				amountOfDays
				fromDate
				toDate
				department
				notification {
					id
					read
				}
			}
			totalPages
			totalRows
		}
	}
`;

function MissionRecordList() {
	const location = useLocation();
	const history = useHistory();
	const urlParams = new URLSearchParams(location.search);
	const currentPage = urlParams.get('page') ? urlParams.get('page') : 1;
	const no = urlParams.get('no') ? urlParams.get('no') : null;
	const reason = urlParams.get('reason') ? urlParams.get('reason') : null;
	const type = urlParams.get('type') ? urlParams.get('type') : null;
	const fromDate = urlParams.get('fromDate') ? urlParams.get('fromDate') : null;
	const toDate = urlParams.get('toDate') ? urlParams.get('toDate') : null;
	const read = urlParams.get('read') ? urlParams.get('read') : null;
	const step = urlParams.get('step') ? urlParams.get('step') : null;
	const status = urlParams.get('status') ? urlParams.get('status') : 'INPROGRESS';
	const modalFilter = useDisclosure();
	const modalExport = useDisclosure();
	const { isOpen, onOpen, onClose } = useDisclosure();
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state.currentLang);
	const { currentUser: user } = useStore((state) => state.currentUser);
	const [rowLimit, setRowLimit] = useState(10);
	const [record, setRecord] = useState([]);
	const [exportRecords, setExportRecords] = useState([]);
	const showSmallScreen = useBreakpointValue({
		base: true,
		sm: true,
		md: false,
		lg: false, // >= 992px
		xl: false, // >= 1280px
	});
	const [loadData, { loading, data }] = useLazyQuery(QUERY_DATA);

	moment.locale(lang === 'kh' ? 'km' : 'en-gb');

	function callbackFilter(e) {
		history.push(`/mission-record${e}`);
	}

	useEffect(() => {
		if (data) {
			setRecord(data?.getMissionRecord?.records);
		}
	}, [data]);

	useEffect(() => {
		if (currentPage) {
			window.scrollTo(0, 0);
			loadData({
				variables: {
					offset: currentPage ? (currentPage - 1) * rowLimit : 0,
					limit: rowLimit,
					no: no ? parseFloat(no) : null,
					category: type || null,
					reason: reason || null,
					fromDate: datePickerFromToToDate(fromDate),
					toDate: datePickerFromToToDate(toDate),
					read: read === 'false' ? false : read === 'true' ? true : null,
					step: step || null,
					status: status || null,
				},
			});
		}
	}, [rowLimit, currentPage, no, reason, fromDate, toDate, type, read, step, status]);

	function handlePageClick(data) {
		let selected = data.selected;
		const page = selected + 1;

		let url = `?page=${page}`;

		if (step) {
			url += `&step=${step}`;
		}

		if (status) {
			url += `&status=${status}`;
		}

		if (no) {
			url += `&no=${no}`;
		}

		if (reason) {
			url += `&reason=${reason}`;
		}

		if (fromDate) {
			url += `&fromDate=${fromDate}`;
		}

		if (toDate) {
			url += `&toDate=${toDate}`;
		}

		if (type) {
			url += `&type=${type}`;
		}

		if (read !== null) {
			const unreadOnly = read ? read : '';
			if (unreadOnly) {
				url += `&read=false`;
			}
		}

		history.push(`/mission-record/list${url}`);
	}

	function handleStatusChange(statusValue) {
		const page = 1;

		let url = `?page=${page}`;

		if (statusValue) {
			url += `&status=${statusValue}`;
		}

		if (no) {
			url += `&no=${no}`;
		}

		if (reason) {
			url += `&reason=${reason}`;
		}

		if (fromDate) {
			url += `&fromDate=${fromDate}`;
		}

		if (toDate) {
			url += `&toDate=${toDate}`;
		}

		if (type) {
			url += `&type=${type}`;
		}

		if (read !== null) {
			const unreadOnly = read ? read : '';
			if (unreadOnly) {
				url += `&read=false`;
			}
		}

		history.push(`/mission-record/list${url}`);
	}

	function panelTabs({ name, value }) {
		const active = status || 'INPROGRESS';
		return (
			<Flex
				className="panel-tab"
				userSelect="none"
				minW="100px"
				onClick={() => handleStatusChange(value)}
				cursor="pointer"
				mb="-2px"
				_hover={{ boxShadow: `inset 0 -2px 0 0 ${value === active ? '#2980b9' : '#bdc3c7'}` }}
				boxShadow={value === active ? 'inset 0 -2px 0 0 #2980b9' : ''}
				pt="16px"
				pb="16px"
				pl="12px"
				pr="12px"
				alignItems="center"
				justifyContent="center"
			>
				<Text>{t(name)}</Text>
			</Flex>
		);
	}

	function getApproveStatus(item) {
		if (item?.status === 'APPROVED') {
			return <Badge colorScheme="green">{t('APPROVEDmr')}</Badge>;
		}

		if (item?.status === 'REJECTED') {
			return <Badge colorScheme="red">{t('REJECTEDmr')}</Badge>;
		}

		switch (item?.step) {
			case 'Participant':
				return <Badge colorScheme="blue">{t('Waiting for participants to accept')}</Badge>;

			case 'Office':
				return <Badge colorScheme="blue">{t('Waiting for division to approve')}</Badge>;

			case 'Department':
				return <Badge colorScheme="blue">{t('Waiting for department to approve')}</Badge>;

			case 'HR':
				return <Badge colorScheme="blue">{t("Waiting for HR's acknowledgement")}</Badge>;

			case 'DirectorGeneral':
				return <Badge colorScheme="blue">{t('Waiting for DG to approve')}</Badge>;

			default:
				return <Badge colorScheme="gray">{t('N/A')}</Badge>;
		}
	}

	const canShowExportButton = user?.role === 'admin' || user?.canExportMissionRecordData === true;

	return (
		<PrintLayout>
			<Container maxW="container.xl" mt="16px" mb="16px">
				<BreadcrumbComponent
					list={[
						{
							name: 'Mission Record',
							path: '#',
						},
					]}
				/>
				<Center className="show-only-printing">
					<Text textAlign="center" color="#62B3ED" fontFamily="Moul" fontSize={18}>
						កំណត់ត្រាបេសកកម្ម
					</Text>
				</Center>
				<Center mt="5">
					<Box
						w="100%"
						bg="white"
						borderRadius={c.borderRadius}
						border="1px solid #dbdbdb"
						boxShadow="0 1px 2px rgba(var(--black, #000), 0.1)"
						pb="32px"
					>
						<Flex
							className="hidden-print responsive-header-for-table"
							bg="#FAFAFA"
							p="16px"
							pt="0"
							pb="0"
							mb="16px"
							alignItems="center"
							justifyContent="space-between"
							borderBottom="1px solid #dbdbdb"
						>
							<Flex className="panel-tab-container" minW="303px">
								{panelTabs({ name: 'In Progress', value: 'INPROGRESS' })}
								{panelTabs({ name: 'mrDone', value: 'DONE' })}
								{panelTabs({ name: 'All', value: 'ALL' })}
							</Flex>
							<Flex className="header-menu" alignItems="center" justifyContent="flex-end" minW="361px">
								<Button
									mr="2"
									onClick={() => window?.print()}
									leftIcon={<FontAwesomeIcon icon={faPrint} style={{ fontSize: 16 }} />}
									colorScheme="gray"
									variant="solid"
									size="sm"
									borderRadius={c.borderRadius}
									border="1px solid #bdc3c7"
								>
									{t('Print')}
								</Button>
								{canShowExportButton && (
									<Button
										mr="2"
										onClick={() => {
											setExportRecords([]);
											modalExport?.onOpen();
										}}
										leftIcon={<FontAwesomeIcon icon={faFileExcel} style={{ fontSize: 16 }} />}
										colorScheme="gray"
										variant="solid"
										size="sm"
										borderRadius={c.borderRadius}
										border="1px solid #bdc3c7"
									>
										{t('Export')}
									</Button>
								)}
								<Button
									onClick={modalFilter?.onOpen}
									leftIcon={<FontAwesomeIcon icon={faSlidersH} style={{ fontSize: 16 }} />}
									colorScheme={`${no || reason || fromDate || toDate || read || type || status === 'APPROVED' || status === 'REJECTED' ? 'yellow' : 'gray'}`}
									variant="solid"
									size="sm"
									mr={user?.position !== '1' ? 2 : 0}
									borderRadius={c.borderRadius}
									border="1px solid #bdc3c7}"
								>
									{t('Filter')}
								</Button>

								{user?.position !== '1' && (
									<Button
										leftIcon={<FontAwesomeIcon icon={faPlus} style={{ fontSize: 16 }} />}
										colorScheme="gray"
										variant="solid"
										size="sm"
										borderRadius={c.borderRadius}
										border="1px solid #bdc3c7"
										onClick={onOpen}
									>
										{t('Create')}
									</Button>
								)}
							</Flex>
						</Flex>

						<Box p="16px">
							<Table variant="simple" className="table-company-proposal-list responsive-table-container">
								<Thead>
									<Tr className="td-as-th">
										<Th align="left">{t('Number')}</Th>
										<Th align="left">{t('Category')}</Th>
										<Th align="left">{t('Reason')}</Th>
										<Th align="left" width="200px">
											{t('Amount of Days')}
										</Th>
										<Th align="left">{t('Date From')}</Th>
										<Th align="right">{t('Date To')}</Th>
										<Th align="right">{t('Status')}</Th>
									</Tr>
								</Thead>
								<Tbody>
									{loading && <LoadingResponsive column={9} />}
									{data?.getMissionRecord?.records?.length <= 0 && (
										<Tr align="left">
											<Text ml={6} mt={2} mb={2}>
												{t('No Data')}
											</Text>
										</Tr>
									)}
									{record?.map((item, index) => {
										return (
											<Tr
												key={index}
												cursor="pointer"
												_hover={{ bg: 'gray.200' }}
												onClick={() => history.push(`/mission-record/detail?id=${item.id}`)}
												style={{
													cursor: 'pointer',
													backgroundColor: item?.notification?.read === false ? '#EEEEEE' : null,
												}}
											>
												<Td>{item?.no ? String(item?.no).padStart(4, '0') : 'N/A'}</Td>
												<Td>
													<Box width={showSmallScreen ? '180px' : '120px'}>{t(CATEGORY_LABELS[item?.category])}</Box>
												</Td>
												<Td style={{ textAlign: 'left' }}>
													<Box width={showSmallScreen ? '180px' : '250px'}>{item?.reason}</Box>
												</Td>
												<Td align="left">{item?.amountOfDays}</Td>
												<Td>{moment(item?.fromDate).format('LLLL')}</Td>
												<Td align="right">{moment(item?.toDate).format('LLLL')}</Td>
												<Td align="right">{getApproveStatus(item)}</Td>
											</Tr>
										);
									})}
								</Tbody>
								<tfoot className="responsive-footer">
									<Tr className="td-as-th">
										<Th align="left">{t('Number')}</Th>
										<Th align="left">{t('Category')}</Th>
										<Th align="left">{t('Reason')}</Th>
										<Th align="left">{t('Amount of Days')}</Th>
										<Th align="left">{t('Date From')}</Th>
										<Th align="right">{t('Date To')}</Th>
										<Th align="right">{t('Status')}</Th>
									</Tr>
								</tfoot>
							</Table>
							<Flex justifyContent="flex-start" className="show-only-printing">
								{t('Total Rows')}: {data?.getMissionRecord?.totalRows}
							</Flex>
							<Flex justifyContent="flex-end" className="hidden-print">
								{t('Total Rows')}: {data?.getMissionRecord?.totalRows}
							</Flex>
							{data?.getMissionRecord?.totalPages > 1 && (
								<Flex justifyContent="flex-end" w="100%" mt="4">
									<ReactPaginate
										previousLabel={<ChevronLeftIcon fontSize="20px" />}
										nextLabel={<ChevronRightIcon fontSize="20px" />}
										breakLabel={'...'}
										breakClassName={'break-me'}
										pageCount={data?.getMissionRecord?.totalPages}
										marginPagesDisplayed={2}
										pageRangeDisplayed={3}
										onPageChange={handlePageClick}
										containerClassName={'pagination'}
										activeClassName={'active'}
										initialPage={(currentPage - 1) | 0}
									/>
								</Flex>
							)}
							<Flex className="hidden-print" justifyContent="flex-end" mt="6">
								<Text mr="2">{t('Show more rows')}</Text>
								<Checkbox
									isChecked={rowLimit > 10}
									onChange={() => {
										handlePageClick({ selected: 0 });
										if (rowLimit > 10) {
											setRowLimit(10);
										} else {
											setRowLimit(300);
										}
									}}
								/>
							</Flex>
						</Box>
					</Box>
				</Center>
			</Container>

			<Modal size="xl" isOpen={isOpen} onClose={onClose}>
				<ModalOverlay />
				<ModalContent borderRadius={c.borderRadius} pt={2}>
					<ModalHeader>{t('Create a record')}</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<Flex justifyContent="center" pb={12}>
							<MissionRecordAdd onCloseModal={onClose} />
						</Flex>
					</ModalBody>
				</ModalContent>
			</Modal>

			<MissionRecordExport isOpen={modalExport?.isOpen} onClose={modalExport?.onClose} />

			<MissionRecordFilter
				callback={callbackFilter}
				isOpen={modalFilter?.isOpen}
				onClose={modalFilter?.onClose}
				no={no}
				reason={reason}
				type={type}
				fromDate={fromDate}
				toDate={toDate}
				read={read}
				status={status}
			/>
		</PrintLayout>
	);
}

export default MissionRecordList;
